import { useEffect, useState, type CSSProperties } from "react";
import { ChevronRight, List, Settings2 } from "lucide-react";
import type { BriefItem } from "./content";
import { useWorkshopModals } from "./modals/useWorkshopModals";
import { modalContentByWorkshop } from "./modals/modal-content";
import {
  resolveField,
  resolveIntroVideoUrl,
} from "./overrides/workshop-override-content";
import {
  getEffectiveWorkshopOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import { useQuestionnaires } from "@/stores/useQuestionnaires";
import { useQuestionnaireResponses } from "@/stores/useQuestionnaireResponses";
import { WorkshopOverrideEditor } from "./overrides/WorkshopOverrideEditor";
import { usePowerUser } from "@/stores/usePowerUser";
import { useAuth } from "@/hooks/use-auth";
import { can } from "@/lib/auth/can";
import { workshopOverrideEditor as editorContent } from "@/content/ui";
import type { Workshop } from "@/content/workshops";

/**
 * Workshop briefing list — pre-workshop materials (video, pre-reading,
 * questionnaire). Ported from the `Brief` panel in `_legacy/wd.html`
 * lines 68-110 — vanilla chrome (`.panel` / `.panel-header` /
 * `.panel-body`) with `.brief-list*` rows ported inline (the legacy
 * `.brief-list*` selectors live in `_legacy/xaes.css` lines 1021-1045
 * but were not promoted to `src/index.css` — we recreate them with
 * Tailwind utilities + CSS variables so the look stays theme-aware
 * without adding a new class).
 *
 * Phase 2 (3/N) wires each row to the shared `useWorkshopModals` store
 * so clicks open the matching modal (pre-reading → Doc, questionnaire
 * → Questionnaire). The intro-video row opens an external URL in a
 * new tab — vanilla pulled the URL from `WS4_SESSION.videoUrl` in
 * `_legacy/ws4-session.js`; until that per-workshop content lands we
 * compose a placeholder from the workshop id.
 */

const ROW_STYLE: CSSProperties = {
  padding: "6px 14px",
  transition: "background 0.15s",
};

const ICON_WRAP_STYLE: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "rgba(var(--accent-true-blue-rgb), 0.12)",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
  color: "var(--accent-true-blue)",
};

const LABEL_STYLE: CSSProperties = {
  fontSize: "var(--fs-sub)",
  fontWeight: 500,
  color: "var(--text-primary)",
  letterSpacing: "0.3px",
};

const META_BASE_STYLE: CSSProperties = {
  fontSize: "var(--fs-sub)",
  letterSpacing: "0.3px",
};

const ARROW_STYLE: CSSProperties = {
  fontSize: "var(--fs-heading)",
  color: "var(--text-muted)",
  lineHeight: 1,
};

function metaColour(status: BriefItem["status"]): string {
  // Legacy maps `.available` and `.complete` meta both to the brand
  // accent — see `_legacy/xaes.css` line 1045 plus the inline meta
  // colours in `_legacy/wd.html` lines 87 (text-blue) and 108
  // (available → true-blue) — but the prompt for this slice wants the
  // `Complete` state read as green (`var(--accent-green)`) to make
  // completion at-a-glance.
  if (status === "complete") return "var(--accent-green)";
  if (status === "available") return "var(--accent-true-blue)";
  return "var(--text-muted)";
}

/**
 * Live meta/status for the questionnaire Brief row — derived from the
 * respondent's persisted answers (`useQuestionnaires`) instead of authored
 * content, which used to hardcode "Complete" (UAT round 1). Returns null
 * when the workshop has no questionnaire content, leaving the row bare
 * like any other meta-less row.
 *
 * "Submitted" outranks the draft-derived states: once the caller's response
 * has gone to the facilitation team (`useQuestionnaireResponses`), that —
 * not the local draft completeness — is what the row should report.
 */
function useQuestionnaireRowState(
  workshopId: string,
): Pick<BriefItem, "meta" | "status"> | null {
  const hydrate = useQuestionnaires((s) => s.hydrate);
  const answers = useQuestionnaires((s) => s.answersByWorkshop[workshopId]);
  const override = useEffectiveWorkshopOverride(workshopId);
  const auth = useAuth();
  const username = auth.user?.username ?? "anon";
  const hydrateResponses = useQuestionnaireResponses((s) => s.hydrate);
  const hasSubmitted = useQuestionnaireResponses((s) =>
    (s.entriesByWorkshop[workshopId as Workshop["id"]] ?? []).some(
      (r) => r.username === username,
    ),
  );
  useEffect(() => {
    void hydrate();
    void hydrateResponses();
  }, [hydrate, hydrateResponses]);
  const content =
    modalContentByWorkshop[workshopId as keyof typeof modalContentByWorkshop];
  if (!content) return null;
  const questionnaire = resolveField(
    override,
    "questionnaire",
    content.questionnaire,
  );
  const total = questionnaire.questions.length;
  if (total === 0) return null;
  if (hasSubmitted) return { meta: "Submitted", status: "complete" };
  const answered = questionnaire.questions.filter(
    (q) => (answers?.[q.id] ?? "").trim().length > 0,
  ).length;
  if (answered === 0) return { meta: "Not started", status: "available" };
  if (answered < total)
    return { meta: `${answered} / ${total} answered`, status: "available" };
  return { meta: "Complete", status: "complete" };
}

type BriefRowProps = {
  item: BriefItem;
  workshopId: string;
};

function BriefRow({ item, workshopId }: BriefRowProps) {
  const Icon = item.icon;
  const open = useWorkshopModals((s) => s.open);
  const questionnaireState = useQuestionnaireRowState(workshopId);
  const live = item.id === "questionnaire" ? questionnaireState : null;
  const meta = live ? live.meta : item.meta;
  const status = live ? live.status : item.status;

  function handleClick(): void {
    if (item.modal) {
      open(item.modal);
      return;
    }
    // URL rows. The intro-video row honours the per-workshop override URL
    // (BFF-shared); an authored link row carries its own `videoUrl`. Either
    // way fall back to a deterministic placeholder so the row always does
    // *something*. `getEffectiveWorkshopOverride` is an imperative read — fine in a
    // click handler (no reactive subscription needed here).
    const fallback = item.videoUrl ?? `https://example.com/intro/${workshopId}`;
    const url =
      item.id === "intro-video"
        ? resolveIntroVideoUrl(getEffectiveWorkshopOverride(workshopId), fallback)
        : (item.videoUrl ?? fallback);
    window.open(url, "_blank");
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      // Stable hook for tests/automation — survives label copy changes
      // (the visible label is content-driven and has been renamed before).
      data-testid={`brief-row-${item.id}`}
      className="flex w-full items-center justify-between text-left hover:bg-[rgba(var(--accent-true-blue-rgb),0.05)] focus-visible:bg-[rgba(var(--accent-true-blue-rgb),0.05)] focus-visible:outline-none cursor-pointer"
      style={ROW_STYLE}
    >
      <span className="flex items-center gap-2">
        <span
          className="flex flex-shrink-0 items-center justify-center"
          style={ICON_WRAP_STYLE}
          aria-hidden
        >
          <Icon style={{ width: 13, height: 13 }} strokeWidth={1.8} />
        </span>
        <span style={LABEL_STYLE}>{item.label}</span>
      </span>
      <span className="flex items-center gap-1.5">
        {meta && (
          <span style={{ ...META_BASE_STYLE, color: metaColour(status) }}>
            {meta}
          </span>
        )}
        <ChevronRight
          aria-hidden
          style={{ ...ARROW_STYLE, width: 14, height: 14 }}
          strokeWidth={1.5}
        />
      </span>
    </button>
  );
}

type BriefPanelProps = {
  items: BriefItem[];
  workshopId: string;
};

export function BriefPanel({ items, workshopId }: BriefPanelProps) {
  // Power-user "Manage Workshop" affordance — gated by power-user mode AND
  // the program-management ability (the same contract as the Manage Crew
  // tab + the program-flow controls). The BFF re-checks the write grant.
  const isPowerUnlocked = usePowerUser((s) => s.isUnlocked);
  const { user } = useAuth();
  const canManage = isPowerUnlocked && can(user, "manage:program");
  const [editorOpen, setEditorOpen] = useState(false);

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="panel-title">
          <List className="panel-title-icon" aria-hidden />
          Brief
        </div>
        <div className="panel-menu" aria-hidden>
          ···
        </div>
      </div>
      <div className="panel-body">
        <div className="flex flex-col py-0.5">
          {items.map((item) => (
            <BriefRow key={item.id} item={item} workshopId={workshopId} />
          ))}
        </div>
        {canManage && (
          <div className="px-3 pt-1.5 pb-1">
            <button
              type="button"
              onClick={() => setEditorOpen(true)}
              className="flex w-full items-center justify-center gap-1.5 rounded border border-dashed border-[rgba(var(--accent-true-blue-rgb),0.35)] py-1.5 font-mono text-fs-label tracking-[0.18em] text-[color:var(--accent-true-blue)] uppercase transition-colors hover:bg-[rgba(var(--accent-true-blue-rgb),0.08)]"
            >
              <Settings2 className="h-3 w-3" aria-hidden />
              {editorContent.triggerLabel}
            </button>
          </div>
        )}
      </div>
      {canManage && (
        <WorkshopOverrideEditor
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          workshopId={workshopId as Workshop["id"]}
        />
      )}
    </div>
  );
}
